/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Collapsible TOC section header with its list of question preview cards.
 *
 * @file QuestionPreviewSection.jsx
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import QuestionPreviewCard from './QuestionPreviewCard';
import { classes } from './styles/QuestionPreviewStyles';
import { formatQuestionCount } from './questionPreviewUtils';

/**
 * One TOC section (e.g. "1.1: The Nature of Physics") with its question count. Clicking the
 * header shows or hides that section's question cards.
 *
 * @param {object} section `{ id, title, questionCount, questions }` from buildChapterSections
 * @param {object} textInfo localized labels (question count, correct answer tag)
 * @param {boolean} defaultExpanded QuestionPreview opens only the first section
 * @param {Function} onSectionExpand called with isExpanded when the user expands or collapses the section
 * @param {Function} onMCQExpand called with isExpanded when the user expands or collapses an MCQ card
 */
const QuestionPreviewSection = ({
  section,
  textInfo,
  defaultExpanded,
  onSectionExpand,
  onMCQExpand
}) => {
  const isDisabled = section.questionCount === 0;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded && !isDisabled);
  const listId = useId();

  return (
    <div className={classes.section}>
      <div className={`${classes.sectionHeader} ${isDisabled ? classes.sectionHeaderDisabled : ''}`}>
        <button
          type="button"
          className={classes.sectionToggle}
          disabled={isDisabled}
          onClick={() => {
            if (isDisabled) {
              return;
            }
            setIsExpanded((prev) => !prev);
            onSectionExpand(!isExpanded);
          }}
          aria-expanded={isDisabled ? false : isExpanded}
          aria-disabled={isDisabled}
          aria-controls={listId}
        >
          <span
            className={`${classes.sectionToggleIcon} ${isExpanded ? classes.expandChevronExpanded : classes.expandChevronCollapsed}`}
            aria-hidden="true"
          >
            <Icon name="ExpandIcon" />
          </span>
          <span className={`${classes.sectionTitle} ${isDisabled ? classes.sectionTextDisabled : ''}`}>{section.title}</span>
        </button>
        <span className={`${classes.sectionCount} ${isDisabled ? classes.sectionTextDisabled : ''}`}>
          {formatQuestionCount(section.questionCount, textInfo)}
        </span>
      </div>

      {isExpanded && !isDisabled && (
        <ul id={listId} className={classes.questionList}>
          {section.questions.map((question) => (
            <li key={question.questionId}>
              <QuestionPreviewCard
                stem={question.stem}
                options={question.options}
                correctAnswerLabel={textInfo.correctAnswerLabel}
                onMCQExpand={onMCQExpand}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const previewSectionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  questionCount: PropTypes.number.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    questionId: PropTypes.string.isRequired,
    stem: PropTypes.string.isRequired,
    options: PropTypes.array
  })).isRequired
});

QuestionPreviewSection.propTypes = {
  section: previewSectionPropType.isRequired,
  textInfo: PropTypes.shape({
    questionLabel: PropTypes.string,
    questionsLabel: PropTypes.string,
    correctAnswerLabel: PropTypes.string
  }).isRequired,
  defaultExpanded: PropTypes.bool,
  onSectionExpand: PropTypes.func,
  onMCQExpand: PropTypes.func
};

QuestionPreviewSection.defaultProps = {
  defaultExpanded: false,
  onSectionExpand: () => {},
  onMCQExpand: () => {}
};

export default QuestionPreviewSection;
